import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Divider,
  Chip
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  Assessment as StatsIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import { API_BASE_URL } from '../config/constants';
import { useAuth } from '../context/AuthContext';

interface Setting {
  _id: string;
  key: string;
  value: any;
  type: string;
  label: string;
  description: string;
  category: string;
  isSystem: boolean;
}

const FigaPointsSettings: React.FC = () => {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [settings, setSettings] = useState<Setting[]>([]);
  const [figaPointsSetting, setFigaPointsSetting] = useState<Setting | null>(null);
  const [newValue, setNewValue] = useState<number>(10);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        throw new Error('Token not found');
      }

      const response = await axios.get(`${API_BASE_URL}/admin/settings`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.status === 'success') {
        setSettings(response.data.data);
        
        // Find figapoints setting (backend key remains the same)
        const figaSetting = response.data.data.find((s: Setting) => s.key === 'figapoints_per_purchase');
        setFigaPointsSetting(figaSetting || null);
        
        if (figaSetting) {
          setNewValue(figaSetting.value);
        }
      } else {
        throw new Error(response.data.message || 'Failed to load settings');
      }
    } catch (error: any) {
      console.error('Error loading settings:', error);
      setError(error.message || 'خطا در بارگذاری تنظیمات');
      enqueueSnackbar('خطا در بارگذاری تنظیمات', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const initializeSettings = async () => {
    setInitializing(true);
    
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        throw new Error('Token not found');
      }

      const response = await axios.post(`${API_BASE_URL}/admin/settings/initialize`, {}, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.status === 'success') {
        enqueueSnackbar('تنظیمات با موفقیت مقداردهی شد', { variant: 'success' });
        loadSettings(); // Reload settings
      } else {
        throw new Error(response.data.message || 'Failed to initialize settings');
      }
    } catch (error: any) {
      console.error('Error initializing settings:', error);
      enqueueSnackbar('خطا در مقداردهی تنظیمات', { variant: 'error' });
    } finally {
      setInitializing(false);
    }
  };

  const updateFigaPointsSetting = async () => {
    if (!figaPointsSetting) return;
    
    setSaving(true);
    
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        throw new Error('Token not found');
      }

      const response = await axios.patch(
        `${API_BASE_URL}/admin/settings/figapoints_per_purchase`,
        { value: newValue },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.status === 'success') {
        enqueueSnackbar('تنظیمات امتیاز با موفقیت بروزرسانی شد', { variant: 'success' });
        loadSettings(); // Reload settings
      } else {
        throw new Error(response.data.message || 'Failed to update setting');
      }
    } catch (error: any) {
      console.error('Error updating setting:', error);
      enqueueSnackbar('خطا در بروزرسانی تنظیمات', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (error && !settings.length) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={loadSettings} startIcon={<RefreshIcon />}>
          تلاش مجدد
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          تنظیمات گلاپوینت
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadSettings}
          disabled={loading}
        >
          بروزرسانی
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {/* GelaPoints Setting Card */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <SettingsIcon sx={{ mr: 1 }} />
                  <Typography variant="h6">
                    تنظیمات امتیاز خرید
                  </Typography>
                </Box>
                
                {figaPointsSetting ? (
                  <>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {figaPointsSetting.description}
                    </Typography>
                    
                    <Box sx={{ mb: 2 }}>
                      <Chip 
                        label={`مقدار فعلی: ${figaPointsSetting.value}`}
                        color={figaPointsSetting.value > 0 ? 'success' : 'error'}
                        variant="outlined"
                      />
                    </Box>
                    
                    <TextField
                      fullWidth
                      type="number"
                      label="مقدار جدید"
                      value={newValue}
                      onChange={(e) => setNewValue(Number(e.target.value))}
                      sx={{ mb: 2 }}
                      inputProps={{ min: 0 }}
                    />
                    
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={updateFigaPointsSetting}
                      disabled={saving || newValue === figaPointsSetting.value}
                    >
                      {saving ? 'در حال ذخیره...' : 'ذخیره تغییرات'}
                    </Button>
                  </>
                ) : (
                  <>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      تنظیمات گلاپوینت پیدا نشد! این میتواند دلیل عدم اعطای امتیاز باشد.
                    </Alert>
                    
                    <Button
                      fullWidth
                      variant="contained"
                      color="warning"
                      startIcon={<SettingsIcon />}
                      onClick={initializeSettings}
                      disabled={initializing}
                    >
                      {initializing ? 'در حال مقداردهی...' : 'مقداردهی تنظیمات'}
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Status Card */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <StatsIcon sx={{ mr: 1 }} />
                  <Typography variant="h6">
                    وضعیت سیستم امتیاز
                  </Typography>
                </Box>
                
                <Box sx={{ mb: 2 }}>
                  {figaPointsSetting ? (
                    figaPointsSetting.value > 0 ? (
                      <Alert severity="success">
                        سیستم امتیازدهی فعال است. کاربران {figaPointsSetting.value} امتیاز در هر خرید دریافت میکنند.
                      </Alert>
                    ) : (
                      <Alert severity="error">
                        سیستم امتیازدهی غیرفعال است. مقدار امتیاز صفر تنظیم شده.
                      </Alert>
                    )
                  ) : (
                    <Alert severity="error">
                      سیستم امتیازدهی تنظیم نشده است.
                    </Alert>
                  )}
                </Box>

                <Divider sx={{ my: 2 }} />
                
                <Typography variant="body2" color="text.secondary">
                  برای فعال‌سازی صحیح سیستم امتیاز:
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  • مقدار امتیاز باید مثبت باشد
                  <br />
                  • سفارشات باید وضعیت "پرداخت شده" داشته باشند
                  <br />
                  • سیستم پرداخت باید صحیح کار کند
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* All Settings */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  تمام تنظیمات سیستم
                </Typography>
                
                {settings.length > 0 ? (
                  <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                    {settings.map((setting) => (
                      <Box key={setting._id} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                        <Typography variant="subtitle2">
                          {setting.label} ({setting.key})
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {setting.description}
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          <Chip 
                            label={`${setting.value} (${setting.type})`}
                            size="small"
                            variant="outlined"
                          />
                          <Chip 
                            label={setting.category}
                            size="small"
                            sx={{ ml: 1 }}
                          />
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    هیچ تنظیماتی یافت نشد
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Container>
  );
};

export default FigaPointsSettings; 